import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Smartphone, Database, Shield, ArrowRight, Phone, Hammer, X, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Services: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [showDemos, setShowDemos] = useState(false);

    const goAuthenticated = (path: string) => {
        if (isAuthenticated) navigate(path);
        else navigate('/login', { state: { from: path } });
    };

    const services = [
        {
            icon: <Monitor size={32} />,
            title: "Création de CV",
            desc: "Créez des CV professionnels qui attirent l'attention des recruteurs.",
            color: "bg-blue-50 text-blue-600",
            href: '/choix-modele' as string | null,
        },
        {
            icon: <Smartphone size={32} />,
            title: "Lettres de Motivation",
            desc: "Rédigez des lettres de motivation personnalisées et convaincantes.",
            color: "bg-purple-50 text-purple-600",
            href: '/creer-lettre' as string | null,
        },
        {
            icon: <Database size={32} />,
            title: "Conseils Carrière",
            desc: "Bénéficiez de conseils d'experts pour booster votre carrière.",
            color: "bg-green-50 text-green-600",
            href: null,
        },
        {
            icon: <Shield size={32} />,
            title: "Optimisation LinkedIn",
            desc: "Optimisez votre profil pour être visible par les chasseurs de têtes.",
            color: "bg-red-50 text-red-600",
            href: null,
        }
    ];

    const demoVideos = [
        {
            title: "Démo Création de CV",
            description: "Découvrez notre éditeur intuitif pour créer un CV d'exception.",
            url: "https://assets.mixkit.co/videos/preview/mixkit-software-developer-working-on-his-laptop-34440-large.mp4"
        },
        {
            title: "Démo Lettre de Motivation",
            description: "Comment générer des lettres convaincantes en un clic.",
            url: "https://assets.mixkit.co/videos/preview/mixkit-man-working-at-his-desk-in-a-modern-office-4840-large.mp4"
        }
    ];

    return (
        <>
            <section id="services" className="py-20 bg-gray-50 relative">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-12">
                        <div className="max-w-xl">
                            <h4 className="text-blue-600 font-semibold uppercase mb-2">Nos Services</h4>
                            <h2 className="text-4xl font-bold text-gray-900 leading-tight">Nous simplifions votre <br /> Recherche d'emploi</h2>
                        </div>
                        <div>
                            <button
                                onClick={() => setShowDemos(true)}
                                className="hidden md:flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors"
                            >
                                <span>Voir des démos</span>
                                <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group"
                                whileHover={{ y: -10 }}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${service.color} transition-transform group-hover:scale-110`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-500 mb-6 leading-relaxed">
                                    {service.desc}
                                </p>

                                {service.href ? (
                                    <button
                                        type="button"
                                        onClick={() => goAuthenticated(service.href!)}
                                        className="inline-flex items-center text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors"
                                    >
                                        Lire la suite <ArrowRight className="ml-2 w-4 h-4" />
                                    </button>
                                ) : (
                                    <div className="inline-flex items-center text-sm font-medium text-orange-500 bg-orange-50 px-3 py-1 rounded-full">
                                        <Hammer className="mr-2 w-3 h-3" />
                                        <span>Bientôt disponible</span>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Blue Banner Strip */}
                <div className="container mx-auto px-4 mt-20">
                    <div className="bg-blue-600 rounded-3xl p-10 md:p-14 flex flex-col md:flex-row items-center justify-between text-white relative overflow-hidden">
                        <div className="relative z-10 flex items-center space-x-6">
                            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                <Phone size={28} />
                            </div>
                            <div>
                                <div className="text-blue-200">Appelez-nous</div>
                                <div className="text-3xl font-bold">+225 07 10 14 59 75</div>
                            </div>
                        </div>
                        <div className="relative z-10 text-center md:text-left my-6 md:my-0">
                            <h3 className="text-2xl font-bold mb-2">Restez connecté avec <br /> les meilleures opportunités</h3>
                        </div>
                        <div className="relative z-10">
                            <button className="bg-white text-blue-900 px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors">
                                Commencer &rarr;
                            </button>
                        </div>

                        {/* Background Pattern */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                    </div>
                </div>
            </section>
            <AnimatePresence>
                {showDemos && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setShowDemos(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-white rounded-[2.5rem] w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900">Démos de nos services</h3>
                                    <p className="text-gray-500 mt-1">Découvrez FlashCV en action</p>
                                </div>
                                <button
                                    onClick={() => setShowDemos(false)}
                                    className="p-3 hover:bg-white shadow-sm hover:shadow-md rounded-full transition-all text-gray-400 hover:text-gray-600"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="p-8 overflow-y-auto max-h-[calc(90vh-120px)] bg-white">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {demoVideos.map((video, index) => (
                                        <motion.div
                                            key={index}
                                            className="space-y-4 group"
                                            initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.1 + 0.2 }}
                                        >
                                            <div className="aspect-video bg-gray-900 rounded-[2rem] overflow-hidden relative shadow-lg group-hover:shadow-2xl transition-all duration-500 border-4 border-white">
                                                <video
                                                    src={video.url}
                                                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                                    controls
                                                    poster="/flashcv-logo.png"
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-transparent transition-colors pointer-events-none">
                                                    <div className="w-16 h-16 bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-500">
                                                        <Play className="text-white fill-white ml-1" size={32} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="px-2">
                                                <h4 className="font-bold text-xl text-gray-900 mb-2 group-hover:text-blue-600 transition-colors uppercase tracking-tight">{video.title}</h4>
                                                <p className="text-gray-500 leading-relaxed text-sm">{video.description}</p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Info Banner */}
                                <div className="mt-12 bg-blue-50 rounded-3xl p-6 flex items-center space-x-4 border border-blue-100">
                                    {/* <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white shrink-0">
                                        <Monitor size={24} />
                                    </div>
                                    <div>
                                        <h5 className="font-bold text-blue-900">Besoin d'aide ?</h5>
                                        <p className="text-blue-700 text-sm">Nos experts sont là pour vous accompagner dans la création de votre CV.</p>
                                    </div> */}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Services;
